import {setInShallowClone} from '../../../util/utility';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type ViewReducerState} from '../reducerSupport';
import {arrayInsert, arrayMove, arrayRemove} from './arrayMove';

export type ActiveData = {
  panelIndex: number;
  panelRef: PanelTypes.Ref;
  sectionRef: PanelBankSectionConfigTypes.Ref;
  section: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized;
};

export type TargetData = {
  panelIndex: number;
  insertDir: 'before' | 'after';
  panelRef: PanelTypes.Ref | null;
  sectionRef: PanelBankSectionConfigTypes.Ref;
  section: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized;
};

export const getMovePanelData = (
  state: ViewReducerState,
  active: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  },
  target: {
    panelRef: PanelTypes.Ref | null;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  }
) => {
  const activeSection =
    state.parts[active.sectionRef.type][active.sectionRef.id];
  const activePanelIndex = activeSection.panelRefs.findIndex(
    panelRef => panelRef.id === active.panelRef.id
  );

  // If the target panelRef is null, we know that the target section is empty
  // and we'll insert the panel at the first position
  const targetSection =
    state.parts[target.sectionRef.type][target.sectionRef.id];
  const targetPanelIndex =
    target.panelRef == null
      ? 0
      : targetSection.panelRefs.findIndex(
          panelRef => panelRef.id === target.panelRef?.id
        );

  const isMovingToSameSection = active.sectionRef.id === target.sectionRef.id;
  const isMovingToSamePosition = activePanelIndex === targetPanelIndex;

  return {
    activePanelIndex,
    activeSection,
    isMovingToSamePosition,
    isMovingToSameSection,
    targetPanelIndex,
    targetSection,
  };
};

export const handleMovePanelToDiffSection = (
  state: ViewReducerState,
  active: ActiveData,
  target: TargetData
): ViewReducerState => {
  // Remove panel from active section
  const newActiveSectionPanelRefs = arrayRemove(
    active.section.panelRefs,
    active.panelIndex
  );

  // Insert panel at the correct position in target section
  const newTargetSectionPanelRefs = arrayInsert(
    target.section.panelRefs,
    active.panelRef,
    target.insertDir,
    target.panelIndex
  );

  // Update the order of panels within the sections using refs
  let newState = setInShallowClone(
    state,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'panelRefs'],
    newActiveSectionPanelRefs
  );
  newState = setInShallowClone(
    newState,
    ['parts', target.sectionRef.type, target.sectionRef.id, 'panelRefs'],
    newTargetSectionPanelRefs
  );

  return newState;
};

export const handleMovePanelToSameSection = (
  state: ViewReducerState,
  active: ActiveData,
  target: TargetData
): ViewReducerState => {
  const newSectionPanelRefs = arrayMove(
    active.section.panelRefs,
    active.panelIndex,
    target.panelIndex
  );

  const newState = setInShallowClone(
    state,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'panelRefs'],
    newSectionPanelRefs
  );

  return newState;
};

export const getUndoData = (
  newState: ViewReducerState,
  active: ActiveData // this refers to the section the panel was moved from
): {
  undoTargetPanelRef: PanelTypes.Ref | null;
  undoInsertDir: 'before' | 'after';
} => {
  const newActiveSectionPanelRefs =
    newState.parts[active.sectionRef.type][active.sectionRef.id].panelRefs;

  if (newActiveSectionPanelRefs.length === 0) {
    return {undoTargetPanelRef: null, undoInsertDir: 'before'};
  }

  // Determine the undo target for moving the panel back to its original position
  // For undo to work correctly, we need to find which panel should be the target
  // to restore the original order
  // If we were originally at position 0, insert before the current first panel
  if (active.panelIndex === 0) {
    return {
      undoTargetPanelRef: newActiveSectionPanelRefs[0],
      undoInsertDir: 'before',
    };
  }

  if (active.panelIndex >= newActiveSectionPanelRefs.length) {
    // If our original position is beyond the current array, insert after the last panel
    return {
      undoTargetPanelRef:
        newActiveSectionPanelRefs[newActiveSectionPanelRefs.length - 1],
      undoInsertDir: 'after',
    };
  }

  // Otherwise, find the panel that's now at our original position and insert before it
  return {
    undoTargetPanelRef: newActiveSectionPanelRefs[active.panelIndex],
    undoInsertDir: 'before',
  };
};
